/**
 * Wall-clock time helpers shared by the Convex functions and the web app.
 *
 * Schedule rules are stored as a weekday plus minutes from local midnight, in
 * `settings.timezone`. Conversion to UTC happens only at materialization
 * (Phase 3), which is what makes DST shifts come out right: a 09:00 lesson is
 * 09:00 local on both sides of a transition even though the UTC instant moves.
 *
 * Pure and dependency-free so both the Convex runtime and the browser can use it.
 */

export const MINUTES_PER_DAY = 1440;

/**
 * Fallback zone for the settings singleton and for any code that runs before it
 * is seeded. Lives here rather than in `settings.ts` so `lessons.ts` can read it
 * without importing the module that imports it back.
 */
export const DEFAULT_TIMEZONE = "Europe/Kyiv";
export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export type CalendarDate = { year: number; month: number; day: number };

/** True for a "YYYY-MM-DD" string that names a real calendar date. */
export function isValidDate(value: string): boolean {
  return parseDate(value) !== null;
}

export function parseDate(value: string): CalendarDate | null {
  if (!DATE_RE.test(value)) {
    return null;
  }
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  // Round-trip through UTC to reject 2026-02-30 and friends.
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/** Lexicographic comparison is chronological for "YYYY-MM-DD". */
export function compareDate(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function formatDate(date: CalendarDate): string {
  const month = String(date.month).padStart(2, "0");
  const day = String(date.day).padStart(2, "0");
  return `${date.year}-${month}-${day}`;
}

/** Days added to a "YYYY-MM-DD", returned in the same format. */
export function addDays(value: string, days: number): string {
  const date = parseDate(value);
  if (date === null) {
    throw new Error(`Invalid date: ${value}`);
  }
  const utc = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return formatDate({
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  });
}

/** 0 = Sunday .. 6 = Saturday. */
export function weekdayOf(value: string): number {
  const date = parseDate(value);
  if (date === null) {
    throw new Error(`Invalid date: ${value}`);
  }
  return new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
}

/** ISO 8601 week number, 1..53. Drives odd/even week parity. */
export function isoWeek(value: string): number {
  const date = parseDate(value);
  if (date === null) {
    throw new Error(`Invalid date: ${value}`);
  }
  const utc = new Date(Date.UTC(date.year, date.month - 1, date.day));
  // Shift to the Thursday of this ISO week; its calendar year is the ISO year.
  const dayOfWeek = (utc.getUTCDay() + 6) % 7; // 0 = Monday
  utc.setUTCDate(utc.getUTCDate() - dayOfWeek + 3);
  const firstThursday = Date.UTC(utc.getUTCFullYear(), 0, 4);
  const firstDayOfWeek = (new Date(firstThursday).getUTCDay() + 6) % 7;
  const firstThursdayOfYear = firstThursday + (3 - firstDayOfWeek) * 86_400_000;
  return 1 + Math.round((utc.getTime() - firstThursdayOfYear) / (7 * 86_400_000));
}

export function parityOf(value: string): "odd" | "even" {
  return isoWeek(value) % 2 === 1 ? "odd" : "even";
}

export function parityMatches(
  ruleParity: "all" | "odd" | "even",
  value: string,
): boolean {
  return ruleParity === "all" || ruleParity === parityOf(value);
}

/** "HH:MM" for minutes from midnight. 1440 renders as "24:00". */
export function formatMinute(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Minutes from midnight for "HH:MM", or null when unparseable. */
export function parseMinute(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (match === null) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (minutes > 59) {
    return null;
  }
  const total = hours * 60 + minutes;
  return total >= 0 && total <= MINUTES_PER_DAY ? total : null;
}

/** The recurrence fields of a schedule rule, as far as occurrence dates go. */
export type RecurrenceWindow = {
  weekday: number;
  effectiveFrom: string;
  effectiveUntil: string;
  weekParity: "all" | "odd" | "even";
};

/** Whether the rule produces an occurrence on `date`. */
export function occursOn(rule: RecurrenceWindow, date: string): boolean {
  return (
    weekdayOf(date) === rule.weekday &&
    compareDate(date, rule.effectiveFrom) >= 0 &&
    compareDate(date, rule.effectiveUntil) <= 0 &&
    parityMatches(rule.weekParity, date)
  );
}

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Offset of `tz` from UTC, in ms, at the given instant. */
function zoneOffsetMs(tz: string, instantMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instantMs));

  const field = (type: string): number => {
    const part = parts.find((p) => p.type === type);
    return part === undefined ? 0 : Number(part.value);
  };

  // Intl renders midnight as hour 24 in some engines.
  const hour = field("hour") % 24;
  const asUtc = Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    hour,
    field("minute"),
    field("second"),
  );
  return asUtc - instantMs;
}

/**
 * UTC ms for a wall-clock local time: `date` at `minute` minutes past midnight
 * in `tz`.
 *
 * A wall-clock time is not always one instant. On a fall-back overlap it names
 * two, and this returns the earlier; on a spring-forward gap it names none, and
 * this resolves forward (02:30 becomes 03:30) using the pre-transition offset.
 * Both match Temporal's "compatible" disambiguation.
 */
export function zonedWallClockToUtc(
  tz: string,
  date: string,
  minute: number,
): number {
  const parsed = parseDate(date);
  if (parsed === null) {
    throw new Error(`Invalid date: ${date}`);
  }
  const naive = Date.UTC(parsed.year, parsed.month - 1, parsed.day, 0, minute);
  const day = 86_400_000;
  // Offsets a day either side bracket any transition on this date.
  const before = zoneOffsetMs(tz, naive - day);
  const after = zoneOffsetMs(tz, naive + day);

  // A candidate is real only if the zone actually uses that offset then.
  const candidates = [naive - before, naive - after].filter(
    (instant) => zoneOffsetMs(tz, instant) === naive - instant,
  );
  if (candidates.length === 0) {
    return naive - before; // inside a gap: shift forward past the transition
  }
  return Math.min(...candidates);
}

/** "YYYY-MM-DD" for the calendar date `instantMs` falls on in `tz`. */
export function dateInZone(tz: string, instantMs: number): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(instantMs));
  // en-CA formats as YYYY-MM-DD, which is the format we store.
  return parts;
}

/** Every "YYYY-MM-DD" from `from` to `to`, both inclusive. */
export function datesBetween(from: string, to: string): string[] {
  const dates: string[] = [];
  let cursor = from;
  // Guard against an inverted or absurd range rather than looping forever.
  for (let i = 0; compareDate(cursor, to) <= 0 && i < 4000; i++) {
    dates.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return dates;
}
